import { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import TwoColumnDescriptionList from "../TwoColumnDescriptionList";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAppointmentsByClientId,
  fetchAppointmentById,
} from "../../../store/thunks/appointmentsThunk";
import FutureAppointments from "../FutureAppointments";
import StepsVerticalListV2 from "../stepsVerticalListV2";
import { AppointmentClientInfo } from "./AppointmentClientInfo";
import { AppointmentInvoices } from "./AppointmentInvoices";
import {
  clearClientAppointments,
  clearSelectedAppointment,
} from "../../../store/slices/appointmentsSlice";
import { AppointmentPayments } from "./AppointmentPayments";
import { fetchInvoicePaymentsByAppointmentId } from "../../../store/thunks/paymentsThunk";
import { fetchInvoicesByAppointmentId } from "../../../store/thunks/invoiceThunk";
import {
  clearAppointmentPayments,
  clearInvoicePayments,
} from "../../../store/slices/paymentsSlice";
import { clearAppointmentInvoices } from "../../../store/slices/invoiceSlice";

export default function AppointmentSummary({
  open,
  onClose,
  onAppointmentClick,
  appointment,
  onReschedule,
}) {
  const dispatch = useDispatch();
  const reduxSelectedAppointment = useSelector(
    (state) => state.appointments.selectedAppointment
  );

  const selectedAppointment = appointment || reduxSelectedAppointment;
  const appointmentInvoices = useSelector(
    (state) => state.invoices.appointmentInvoices
  );
  const futureAppointments = useSelector(
    (state) => state.appointments.clientAppointments
  );
  const payments = useSelector((state) => state.payments.appointmentPayments);

  // Function to refresh all appointment data
  const refreshAppointmentData = async () => {
    if (selectedAppointment?._id) {
      // Refresh the specific appointment
      await dispatch(fetchAppointmentById(selectedAppointment._id));

      // Refresh related data
      if (selectedAppointment.client?._id) {
        dispatch(fetchAppointmentsByClientId(selectedAppointment.client._id));
      }
      dispatch(fetchInvoicesByAppointmentId(selectedAppointment._id));
      dispatch(fetchInvoicePaymentsByAppointmentId(selectedAppointment._id));
    }
  };

  // useEffect to fetch data when component mounts or when selectedAppointment changes
  useEffect(() => {
    if (selectedAppointment?._id) {
      refreshAppointmentData();
    }
  }, [selectedAppointment?._id]);

  // Add effect to refresh data when dialog opens
  useEffect(() => {
    if (open && selectedAppointment?._id) {
      refreshAppointmentData();
    }
  }, [open]);

  const handleRescheduleClick = () => {
    onClose();
    onReschedule(selectedAppointment);
  };

  const handleClose = () => {
    dispatch(clearAppointmentPayments());
    dispatch(clearAppointmentInvoices());
    dispatch(clearInvoicePayments());
    onClose();
  };

  console.log("[APPOINTMENT SUMMARY]: This is the appointment", appointment);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-600/75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full">
                <Dialog.Panel className="pointer-events-auto w-[70vw] h-full flex flex-col bg-white shadow-lg">
                  <div className="flex-1 flex flex-col overflow-y-scroll bg-gray-50/30">
                    <div className="px-4 sm:px-6 border-b border-gray-100 bg-white/50 backdrop-blur-sm">
                      <div className="flex items-center justify-between py-3">
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Appointment Summary
                        </Dialog.Title>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            onClick={handleClose}
                            className="rounded-sm text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-950 focus:ring-offset-1 p-1 hover:bg-gray-100 transition-all">
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="relative flex-1 px-4 sm:px-6 py-4">
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        <div className="lg:col-span-4 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                          <AppointmentClientInfo />
                        </div>
                        <div className="lg:col-span-4 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                          {(selectedAppointment.history?.some(
                            (entry) =>
                              entry.status === "Treated" ||
                              entry.status === "Cancelled - Charge Fee" ||
                              entry.status === "No Show - Charge Fee"
                          ) ||
                            appointmentInvoices?.length > 0) && (
                            <AppointmentInvoices />
                          )}
                        </div>

                        <div className="lg:col-span-4 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                          {payments?.length > 0 && <AppointmentPayments />}
                        </div>
                        <div className="lg:col-span-2 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                          <TwoColumnDescriptionList
                            type="appointment"
                            appointment={selectedAppointment}
                            onReschedule={handleRescheduleClick}
                          />
                          <FutureAppointments
                            appointments={futureAppointments}
                            onAppointmentClick={onAppointmentClick}
                          />
                        </div>
                        <div className="lg:col-span-2 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                          <StepsVerticalListV2
                            appointment={selectedAppointment}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
